import React from 'react';
import { Alert, Badge, CardMedia, Typography } from '@one-thd/sui-atomic-components';
import { ALERT_MESSAGES, UNAVAILABLE_IMAGE } from '../../constants';
import { generateItemImageUrl, getItemQuantity } from '../../utils/general';
import { IDistributionViewLineItem } from '../../models/active-orders';

const MOBILE_IMAGES_CONTAINER_HEIGHT = 'sui-h-[78px]';
const DESKTOP_IMAGES_CONTAINER_HEIGHT = 'sui-h-[80px]';

const sizeMapMobile: { [key: number]: number; default: number } = {
  1: 78,
  2: 63,
  3: 41,
  4: 33,
  default: 33,
};
const gridMapMobile: { [key: number]: string; default: string } = {
  2: '1fr 1fr',
  3: '1fr 1fr 1fr',
  4: '1fr 1fr',
  5: '1fr 1fr 1fr',
  default: '1fr 1fr 1fr',
};

const getMaxSizeMobile = (count: number) => sizeMapMobile[count] ?? sizeMapMobile.default;
const getGridColsMobile = (count: number) => gridMapMobile[count] ?? gridMapMobile.default;

const ItemQuantityBadge = ({ quantity, isMobile }: { quantity: number; isMobile: boolean }) => {
  if (quantity <= 1) {
    return null;
  }

  return (
    <div
      className={`sui-absolute ${isMobile ? 'sui--right-2 sui--top-2' : 'sui--right-1 sui--top-1'}`}
    >
      <Badge
        variant="strong"
        size={isMobile ? 'small' : 'base'}
        color="primary"
        data-testid="item-quantity-badge"
      >
        {quantity > 99 ? '99+' : quantity}
      </Badge>
    </div>
  );
};

const RemainingImagesBadge = ({ count, size }: { count: number; size: number }) => (
  <div
    className="sui-bg-subtle sui-flex sui-justify-center sui-items-center"
    data-testid="remaining-images-badge"
    style={{
      width: size,
      height: size,
      borderRadius: 2,
    }}
  >
    <Typography variant="body-xs" weight="bold">
      +{count}
    </Typography>
  </div>
);

const ItemImage = ({
  src,
  alt,
  quantity,
  size,
  isMobile,
  fluid = false,
}: {
  src: string;
  alt: string;
  quantity: number;
  size: number;
  isMobile: boolean;
  fluid?: boolean;
}) => (
  <div className="sui-relative" data-testid="item-image-container">
    <CardMedia
      src={src}
      alt={alt}
      data-testid="item-image"
      className="sui-object-cover sui-rounded sui-aspect-square"
      style={fluid ? { width: '100%', height: 'auto', maxWidth: size, maxHeight: size } : { width: size, height: size }}
    />
    <ItemQuantityBadge quantity={quantity} isMobile={isMobile} />
  </div>
);

const renderDesktopImages = (lineItems: IDistributionViewLineItem[]) => {
  const maxDesktop = 6;
  const hasOverflow = lineItems.length > maxDesktop;
  const maxVisible = hasOverflow ? maxDesktop - 1 : maxDesktop;
  const visibleImages = lineItems.slice(0, maxVisible);
  const remaining = lineItems.length - visibleImages.length;

  return (
    <div
      className={`
        sui-grid
        sui-grid-cols-6
        sui-gap-2
        sui-items-center
        sui-w-full
        sui-content-center
        ${DESKTOP_IMAGES_CONTAINER_HEIGHT}
        `}
    >
      {visibleImages.map((item, id) => (
        <ItemImage
          key={`item-image-${id}`}
          src={generateItemImageUrl(item)}
          alt={item?.name || UNAVAILABLE_IMAGE}
          quantity={getItemQuantity(item) ?? 0}
          size={48}
          isMobile={false}
        />
      ))}

      {remaining > 0 && <RemainingImagesBadge count={remaining} size={48} />}
    </div>
  );
};

const renderMobileImages = (lineItems: IDistributionViewLineItem[]) => {
  const maxMobile = 6;

  const visible = lineItems.slice(0, lineItems.length > maxMobile ? maxMobile - 1 : maxMobile);

  const remaining = lineItems.length > maxMobile ? lineItems.length - (maxMobile - 1) : 0;

  const slots = visible.length + (remaining > 0 ? 1 : 0);
  const size = getMaxSizeMobile(slots);
  const gridTemplateColumns = getGridColsMobile(slots);

  // Single item
  if (lineItems.length === 1) {
    const item = lineItems[0];
    return (
      <div className={`sui-flex sui-w-full sui-justify-center ${MOBILE_IMAGES_CONTAINER_HEIGHT} sui-items-center`}>
        <ItemImage
          src={generateItemImageUrl(item)}
          alt={item?.name || UNAVAILABLE_IMAGE}
          quantity={getItemQuantity(item) ?? 0}
          size={size}
          isMobile
          fluid
        />
      </div>
    );
  }

  // Multiple items
  return (
    <div
      className={`sui-w-full sui-inline-grid sui-gap-2 sui-justify-items-center ${MOBILE_IMAGES_CONTAINER_HEIGHT} sui-content-center`}
      style={{ gridTemplateColumns }}
    >
      {visible.map((item, id) => (
        <ItemImage
          key={`item-image-${id}`}
          src={generateItemImageUrl(item)}
          alt={item?.name || UNAVAILABLE_IMAGE}
          quantity={getItemQuantity(item) ?? 0}
          size={size}
          isMobile
          fluid
        />
      ))}

      {remaining > 0 && <RemainingImagesBadge count={remaining} size={33} />}
    </div>
  );
};

export const ItemImages = ({ lineItems, isMobile }: { lineItems: IDistributionViewLineItem[]; isMobile: boolean }) => {
  if (!lineItems?.length) {
    return null;
    // TODO: Uncomment when error handling is implemented
    // return (
    //   <div className="sui-flex sui-flex-col sui-w-full">
    //     <Alert status="error">{ALERT_MESSAGES.PRODUCT_DETAILS_UNAVAILABLE}</Alert>
    //   </div>
    // );
  }

  const filteredLineItems = lineItems.filter((item) => getItemQuantity(item) !== undefined);

  if (!filteredLineItems.length) {
    return null;
  }

  return isMobile ? renderMobileImages(filteredLineItems) : renderDesktopImages(filteredLineItems);
};
